/**
 * Funder Coverage — the Plaid-style coverage list, read from the platform.
 *
 * The site used to say "25 California Medi-Cal plans mapped" and stop, which
 * undersold the platform and invited the obvious question: what about every
 * other state? The answer has three layers, and this page states each with
 * what is live today:
 *
 *   1. Reach   — a national clearinghouse network already connects to nearly
 *                every U.S. payer for eligibility, claims and remittance.
 *   2. Rules   — each funder's menu, rates, modifiers and filing windows are
 *                rows with effective dates, not code. California's Medi-Cal
 *                plans are mapped; an agent drafts the rows for the next
 *                plan from its own manual and a person approves them.
 *   3. Channel — each funder is sent work the way it takes it.
 *
 * Every count comes from GET /api/public/funder-coverage (aggregates only).
 * Without it the page shows only what is already true, never a national
 * number it did not read.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Globe2, Network, Scale, Send, UserCheck } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { StatusPill } from '@/components/marketing/CapabilitySpine';
import FunderDiscovery from '@/components/marketing/FunderDiscovery';
import InterfaceBuilder from '@/components/marketing/InterfaceBuilder';
import useFunderCoverage, { plansWithOwnRules, type ChannelStatus } from '@/hooks/useFunderCoverage';
import { contactHref } from '@/components/marketing/siteFacts';
import type { CapabilityStatus } from '@/components/marketing/siteFacts';

const toPill = (s: ChannelStatus): CapabilityStatus => (s === 'live' ? 'Live' : s === 'pilot' ? 'Pilot' : 'Planned');
const n = (v: number) => v.toLocaleString();

const ADD_A_FUNDER = [
  {
    icon: Globe2,
    title: 'Find it in the national directory',
    body: 'The payer list is synced from the clearinghouse network, with the states each payer operates in, its Medicaid and Medicare programs, and which transactions it accepts electronically.',
  },
  {
    icon: Network,
    title: 'Add it to the organization’s funder book',
    body: 'Which funders this organization bills, under which programs, and the channel each one takes. Enrollment with the payer is tracked on the same row.',
  },
  {
    icon: Bot,
    title: 'An agent drafts the rules',
    body: 'From the payer’s own provider manual or policy letter, an agent proposes rule rows: required modifiers, place of service, filing window, authorization, attachments. Every proposal quotes the sentence it came from.',
  },
  {
    icon: UserCheck,
    title: 'A person approves them',
    body: 'Nothing an agent drafts reaches a claim until a billing lead approves it. Rejected proposals are kept, so the reason is on record. Approved rules carry an effective date.',
  },
  {
    icon: Send,
    title: 'Dry run, then submit',
    body: 'Before the first real claim, the dispatcher shows which channel it would use and why. Then the same submission record goes out the funder’s own way.',
  },
];

export default function FunderCoveragePage() {
  usePageMeta(SEO_ROUTES['/funder-coverage']);
  const { coverage, live } = useFunderCoverage();
  const { mapped_plans: mp, directory: dir } = coverage;
  const synced = dir.payers > 0;

  return (
    <div className="bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="bg-paper border-b border-v2border text-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-4">Funder coverage</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Every funder has its own interface.<br />
            <span className="text-v2accent">Adding one is configuration, not a release.</span>
          </h1>
          <p className="text-xl text-ink-500 max-w-3xl">
            A national clearinghouse network already reaches nearly every U.S. payer for eligibility, claims and remittance.
            What differs from funder to funder &mdash; the service menu, the rates, the modifiers, the filing window, the
            channel &mdash; is held as data. California&rsquo;s Medi-Cal plans are mapped today. For the next plan, an agent
            drafts the rules from the plan&rsquo;s own manual and a person approves them.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-4xl font-bold text-ink">{n(mp.count)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">plans with rules held as data</p>
              <p className="text-xs text-gray-400 mt-1">{mp.states.join(', ')}</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-ink">{synced ? n(dir.payers) : '—'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">payers in the national directory</p>
              {!synced && <p className="text-xs text-gray-400 mt-1">Directory sync pending</p>}
            </div>
            <div>
              <p className="text-4xl font-bold text-ink">{synced ? n(dir.states) : '—'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">states and territories covered</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-ink">{synced ? n(dir.medicaid) : '—'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Medicaid payers</p>
            </div>
          </div>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
            {live
              ? `Read from the platform${coverage.as_of ? ` on ${new Date(coverage.as_of).toLocaleDateString()}` : ''}.`
              : 'Showing what is mapped today; live counts load from the platform.'}
            {synced && dir.last_synced_at && ` Directory last synced ${new Date(dir.last_synced_at).toLocaleDateString()}.`}
          </p>
        </div>
      </section>

      <InterfaceBuilder />

      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-10 text-center">Three layers, every funder</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6">
              <Globe2 className="w-7 h-7 text-v2accent mb-3" aria-hidden="true" />
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Reach</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Standard X12 transactions through national clearinghouses: eligibility (270/271), claims (837P), claim status
                (276/277) and remittance (835).
              </p>
              {synced && (
                <ul className="text-sm space-y-1 text-gray-700 dark:text-gray-200">
                  <li>{n(dir.eligibility)} payers accept electronic eligibility</li>
                  <li>{n(dir.claims)} accept electronic claims</li>
                  <li>{n(dir.remittance)} send electronic remittance</li>
                </ul>
              )}
              {coverage.clearinghouses.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {coverage.clearinghouses.map((c) => (
                    <li key={c.key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-200">{c.label}</span>
                      <StatusPill status={toPill(c.status)} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6">
              <Scale className="w-7 h-7 text-v2accent mb-3" aria-hidden="true" />
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Rules</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Each funder&rsquo;s service menu, rates, modifiers, place of service, authorization and filing window, as rows
                with effective dates and the source they came from. A rule change is an edit, not a deploy.
              </p>
              <ul className="text-sm space-y-1 text-gray-700 dark:text-gray-200">
                <li>{n(mp.count)} plans mapped; {n(plansWithOwnRules(mp))} with plan-specific rules</li>
                {(mp.with_drafts ?? 0) > 0 && <li>of those, {n(mp.with_drafts ?? 0)} drafted by agents and awaiting approval</li>}
                {coverage.rule_proposals.pending > 0 && <li>{n(coverage.rule_proposals.pending)} agent-drafted rules awaiting approval</li>}
                {mp.rule_rows > 0 && <li>{n(mp.rule_rows)} rule rows in force</li>}
                {coverage.rule_proposals.approved > 0 && <li>{n(coverage.rule_proposals.approved)} agent-drafted rules approved</li>}
              </ul>
            </div>
            <div className="rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6">
              <Send className="w-7 h-7 text-v2accent mb-3" aria-hidden="true" />
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Channel</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Most plans here do not require an 837 &mdash; DHCS bars them from requiring one &mdash; so each funder gets the
                same submission record the way it takes it.
              </p>
              <ul className="space-y-2">
                {coverage.channels.map((c) => (
                  <li key={c.key} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-200">{c.label}</span>
                    <StatusPill status={toPill(c.status)} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">How a new funder is added</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-10 max-w-3xl">
            The same five steps whether it is a Medicaid plan in another state, a county contract or a Medicare Advantage plan.
            Every plan mapped makes the next one shorter.
          </p>
          <ol className="space-y-4">
            {ADD_A_FUNDER.map((s, i) => (
              <li key={s.title} className="flex gap-4 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <span className="w-10 h-10 shrink-0 rounded-full bg-v2accent-soft flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-v2accent" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-gray-100">
                    <span className="text-gray-400 mr-2">{i + 1}.</span>
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <FunderDiscovery />

      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Funders that do not take claims</h2>
          <p className="text-gray-600 dark:text-gray-300">
            County contracts and foundation grants are funders too, and most never see an 837. Each service on the ledger is
            attributed to the contract or grant that pays for it, and reported in that funder&rsquo;s own template. Cost
            allocation and grant drawdown &mdash; what funded this staff hour &mdash; are on the 2027 roadmap.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Tell us your funders</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Send the plans, counties and grants you bill. We will come back with which are mapped, which are reachable today,
            and what it takes to add the rest.
          </p>
          <Link
            to={contactHref('deliver', 'funder book')}
            className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
          >
            Map my funders
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
